import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
}));

function Competencies() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCompetency, setSelectedCompetency] = useState(null);
  const [openAddNewCompetency, setOpenAddNewCompetency] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal state
  const [newCompetency, setNewCompetency] = useState("");
  const [saving, setSaving] = useState(false);

  const itemsPerPage = 5;

  useEffect(() => {
    fetchCompetencies();
  }, []);

  const fetchCompetencies = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/competencies");
      const data = await res.json();
      setCompetencies(data.recordset || data);
    } catch (error) {
      console.error("Error fetching competencies:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCompetencies = competencies.filter((item) =>
    item.Competency?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedCompetencies = [...filteredCompetencies].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedCompetencies.slice(indexOfFirstItem, indexOfLastItem);

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedCompetency(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCompetency(null);
  };

  const handleAddNew = () => setOpenAddNewCompetency(true);

  const handleSaveCompetency = async () => {
    if (!newCompetency.trim()) return;

    try {
      setSaving(true);
      await fetch("/api/support/competencies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Competency: newCompetency }),
      });

      setNewCompetency("");
      setOpenAddNewCompetency(false);
      fetchCompetencies();
    } catch (error) {
      console.error("Error saving competency:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "white", borderRadius: "16px", p: 3, mb: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Competencies
        </Typography>

        <TextField
          size="small"
          placeholder="Search competency..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          sx={{ minWidth: 280 }}
        />

        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddNew}>
          Add New
        </Button>
      </Box>

      {/* Table */}
      <ResponsiveTableContainer component={Paper}>
        <Table size={isMobile ? "small" : "medium"}>
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.primary.light }}>
              <ModernTableCell sx={{ color: "white" }}>ID</ModernTableCell>
              <ModernTableCell sx={{ color: "white" }}>Competency</ModernTableCell>
              <ModernTableCell sx={{ color: "white" }}>Action</ModernTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" mt={1}>
                    Loading competencies...
                  </Typography>
                </ModernTableCell>
              </TableRow>
            ) : currentItems.length ? (
              currentItems.map((item, index) => (
                <StyledTableRow key={item.id}>
                  <ModernTableCell>{indexOfFirstItem + index + 1}</ModernTableCell>
                  <ModernTableCell>{item.Competency}</ModernTableCell>
                  <ModernTableCell>
                    <Tooltip title="More actions" arrow>
                      <IconButton onClick={(e) => handleMenuOpen(e, item)}>
                        <MoreVertIcon fontSize={isMobile ? "small" : "medium"} />
                      </IconButton>
                    </Tooltip>
                  </ModernTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <ModernTableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  No competencies found
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Pagination
          count={Math.ceil(filteredCompetencies.length / itemsPerPage)}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
          color="primary"
          size={isMobile ? "small" : "medium"}
        />
      </Box>

      {/* Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleMenuClose}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <DeleteIcon fontSize="small" color="error" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* ADD NEW MODAL */}
      <Dialog
        open={openAddNewCompetency}
        onClose={() => setOpenAddNewCompetency(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: theme.palette.primary.main,
            color: "white",
          }}
        >
          <Typography fontWeight={700}>Add New Competency</Typography>
          <IconButton onClick={() => setOpenAddNewCompetency(false)} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>

          </Typography>

          <TextField
            fullWidth
            label="Competency"
            placeholder="e.g. Teamwork, Leadership, Problem Solving"
            value={newCompetency}
            onChange={(e) => setNewCompetency(e.target.value)}
            autoFocus
          />
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenAddNewCompetency(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveCompetency}
            disabled={!newCompetency.trim() || saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Competencies;
